import { supabase, SUPABASE_CONFIG } from './supabaseClient'

export const audioService = {
  // 标准化MIME类型 - 将浏览器变体转换为Supabase Storage支持的标准类型
  normalizeAudioMimeType(mimeType) {
    const mimeMap = {
      // MP3 variants
      'audio/x-mp3': 'audio/mpeg',
      'audio/mp3': 'audio/mpeg',
      
      // WAV variants  
      'audio/x-wav': 'audio/wav',
      'audio/wave': 'audio/wav',
      
      // M4A variants
      'audio/x-m4a': 'audio/m4a',  // Supabase Storage supports audio/m4a
      'audio/mp4': 'audio/m4a',
      'audio/mp4a-latm': 'audio/m4a',
      
      // AAC variants
      'audio/x-aac': 'audio/aac',
      'audio/aacp': 'audio/aac',
      
      // OGG variants
      'audio/x-ogg': 'audio/ogg',
      
      // FLAC variants
      'audio/x-flac': 'audio/flac'
    }
    
    return mimeMap[mimeType] || mimeType
  },

  // 验证音频文件
  validateAudioFile(file) {
    const errors = []
    
    // 检查文件类型 - 使用标准化的MIME类型进行验证
    const normalizedType = this.normalizeAudioMimeType(file.type)
    if (!SUPABASE_CONFIG.allowedFileTypes.includes(file.type) && 
        !SUPABASE_CONFIG.allowedFileTypes.includes(normalizedType)) {
      errors.push(`不支持的文件类型: ${file.type}`)
    }
    
    // 检查文件大小
    if (file.size > SUPABASE_CONFIG.maxFileSize) {
      const maxSizeMB = Math.round(SUPABASE_CONFIG.maxFileSize / 1024 / 1024)
      errors.push(`文件大小超过限制 (最大 ${maxSizeMB}MB)`)
    }
    
    // 检查文件名
    if (!file.name || file.name.length > 255) {
      errors.push('文件名无效或过长')
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  },

  // 获取音频文件时长
  async getAudioDuration(file) {
    return new Promise((resolve, reject) => {
      const audio = new Audio()
      const url = URL.createObjectURL(file)
      
      audio.addEventListener('loadedmetadata', () => {
        URL.revokeObjectURL(url)
        resolve(audio.duration)
      })
      
      audio.addEventListener('error', () => {
        URL.revokeObjectURL(url)
        reject(new Error('无法读取音频文件'))
      })
      
      audio.src = url
    })
  },

  // 上传音频文件到Supabase Storage
  async uploadAudioFile(file, userId, onProgress) {
    try {
      // 验证文件
      const validation = this.validateAudioFile(file)
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '))
      }

      // 生成文件路径
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
      const filePath = `${userId}/${fileName}`

      // 标准化MIME类型并创建新的Blob对象
      const normalizedMimeType = this.normalizeAudioMimeType(file.type)
      const normalizedFile = new Blob([file], { type: normalizedMimeType })
      
      console.log(`🎵 文件上传: 原始MIME类型 ${file.type} -> 标准化为 ${normalizedMimeType}`)

      // 上传文件
      const { data, error } = await supabase.storage
        .from(SUPABASE_CONFIG.buckets.audioFiles)
        .upload(filePath, normalizedFile, {
          cacheControl: '3600',
          upsert: false,
          onUploadProgress: onProgress
        })

      if (error) throw error

      return {
        path: data.path,
        fileName: file.name,
        fileSize: file.size,
        fullPath: data.fullPath
      }
    } catch (error) {
      console.error('文件上传失败:', error)
      throw error
    }
  },

  // 删除音频文件
  async deleteAudioFile(filePath) {
    try {
      const { error } = await supabase.storage
        .from(SUPABASE_CONFIG.buckets.audioFiles)
        .remove([filePath])

      if (error) throw error
    } catch (error) {
      console.error('文件删除失败:', error)
      throw error
    }
  },

  // 获取音频文件下载URL
  async getAudioFileUrl(filePath, expiresIn = 3600) {
    try {
      const { data, error } = await supabase.storage
        .from(SUPABASE_CONFIG.buckets.audioFiles)
        .createSignedUrl(filePath, expiresIn)

      if (error) throw error
      return data.signedUrl
    } catch (error) {
      console.error('获取文件URL失败:', error)
      throw error
    }
  }
}